import { useEffect, useRef, useState } from "preact/hooks";
import { chatStore, detectProductTitle } from "./store.ts";
import { track } from "./analytics.ts";

/**
 * Inline PDP chat block — Sephora-style entry point that lives in the
 * page flow on product detail pages. Dispatches into the floating modal
 * via chatStore so the actual conversation surface is shared.
 *
 * When the shopper clicks a chip or hits send:
 *   1. We push the message text + product handle into chatStore.
 *   2. The floating modal listens, opens itself, fires the message.
 *
 * The inline block doesn't render its own conversation. It's a launcher.
 */

const SUGGESTED_QUESTIONS = [
  "How do I use this?",
  "What are the benefits?",
  "Is it good for sensitive skin?",
  "What's in this?",
  "Is it fragrance-free?",
];

export function PdpInline(props: { productHandle: string | null }) {
  const [input, setInput] = useState("");
  const [productTitle, setProductTitle] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Track view-once: we don't want to spam pdp_inline_view every time the
  // block scrolls in and out. Fire when it first becomes visible.
  const viewedRef = useRef(false);

  useEffect(() => {
    setProductTitle(detectProductTitle());
  }, [props.productHandle]);

  // Fire `pdp_inline_view` when the block first scrolls into the viewport.
  // This is the denominator for "of N shoppers who SAW the inline block,
  // how many engaged?" — much cleaner than counting page loads, since the
  // block can be off-screen for shoppers who buy without scrolling.
  useEffect(() => {
    if (!rootRef.current || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            track("pdp_inline_view", {
              productHandle: props.productHandle ?? undefined,
            });
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(rootRef.current);
    return () => obs.disconnect();
  }, [props.productHandle]);

  function fire(text: string, source: "inline-pdp-chip" | "inline-pdp-send"): void {
    if (!text.trim()) return;
    // Per-surface tracking so we can see whether shoppers prefer chips or
    // typing into the inline block. The downstream modal_open event will
    // also fire (with the same "inline-pdp-chip" source) so we get a
    // funnel from this surface.
    track(source === "inline-pdp-chip" ? "inline_chip_click" : "inline_send_click", {
      source,
      productHandle: props.productHandle ?? undefined,
      properties:
        source === "inline-pdp-chip"
          ? { chip_text: text.trim().slice(0, 100) }
          : { length_bucket: text.length < 60 ? "s" : "m" },
    });
    chatStore.setRequest({
      text: text.trim(),
      productHandle: props.productHandle ?? undefined,
    });
    setInput("");
  }

  return (
    <div class="pdp-inline" role="region" aria-label="Ask AMP about this product" ref={rootRef}>
      <div class="pdp-inline-header">
        <span class="pdp-sparkle" aria-hidden="true">
          <SparkleIcon />
        </span>
        <h3 class="pdp-title">Try our AI Beauty Chat</h3>
        <span class="pdp-new-badge" aria-label="New feature">NEW</span>
      </div>

      <form
        class="pdp-input-row"
        onSubmit={(e) => {
          e.preventDefault();
          fire(input, "inline-pdp-send");
        }}
      >
        <input
          type="text"
          class="pdp-input"
          placeholder={
            productTitle
              ? `Ask any question about ${truncate(productTitle, 40)}`
              : "Ask any question about this product"
          }
          value={input}
          onInput={(e) => setInput((e.target as HTMLInputElement).value)}
          aria-label="Question about this product"
        />
        <button
          type="submit"
          class="pdp-send"
          aria-label="Send"
          disabled={!input.trim()}
        >
          <ArrowUpIcon />
        </button>
      </form>

      <div class="pdp-chips" role="list">
        {SUGGESTED_QUESTIONS.map((q) => (
          <button
            key={q}
            class="pdp-chip"
            role="listitem"
            onClick={() => fire(q, "inline-pdp-chip")}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1).trimEnd() + "…";
}

function SparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* 4-point star — friendlier than a generic asterisk */}
      <path
        d="M12 2L13.8 9.3L21 11L13.8 12.7L12 20L10.2 12.7L3 11L10.2 9.3L12 2Z"
        fill="currentColor"
      />
      <path
        d="M19 14L19.6 16.4L22 17L19.6 17.6L19 20L18.4 17.6L16 17L18.4 16.4L19 14Z"
        fill="currentColor"
        opacity="0.6"
      />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}
